// Passphrase-protected vault for aggregator secrets (Plaid client_id +
// secret + access_tokens; SimpleFIN access URL once C6 migrates it).
//
// Layered on Web Crypto's PBKDF2 + AES-GCM primitives — no third-party
// crypto. Identical posture to Bitwarden's web vault, MetaMask's keyring,
// 1Password web: derive a key from the user's passphrase, encrypt secrets
// at rest in IndexedDB, hold the key in memory only while the app is
// unlocked.
//
// THREAT MODEL this closes vs the prior plaintext-IndexedDB model:
//   - XSS on this origin (foreign script slipped in somehow) can no
//     longer dump credentials from IndexedDB — the at-rest ciphertext
//     is useless without the derived key, which never leaves memory.
//   - A user who clones the IndexedDB store to a different machine
//     cannot read the secrets without the passphrase.
//
// What it does NOT close:
//   - A live XSS that reads the in-memory key (would need a running
//     attack while the vault is unlocked).
//   - A user choosing a weak passphrase. PBKDF2 at 600k iterations
//     makes a single guess take ~250ms on a 2024-era laptop; offline
//     brute force is still possible against weak passphrases.
//   - Malicious browser extensions that hook fetch / DOM (out of any
//     web app's reach).
//
// Crypto choices:
//   - PBKDF2-HMAC-SHA256, 600,000 iterations — matches Bitwarden's
//     post-2023 default. Stored alongside the salt so future-us can
//     upgrade iteration counts without breaking older vaults.
//   - 128-bit random salt (one per vault, written on initialize).
//   - 256-bit derived key.
//   - AES-256-GCM, 96-bit random IV per encrypted item (never reused).
//
// Storage layout (IndexedDB via idb-keyval, origin-scoped):
//   pf:vault-meta:salt       base64 — 16 random bytes
//   pf:vault-meta:check      {iv, ct} — encryption of a known sentinel
//                            used to verify the passphrase on unlock
//   pf:vault-meta:iter       number — PBKDF2 iteration count used
//   pf:vault:<name>          {iv, ct} — encrypted JSON for a tenant key
//
// Tenants (plaid_storage, simplefin_storage) interact via the high-level
// API:
//   await vault.initialize(passphrase)   — first run, sets up the vault
//   await vault.unlock(passphrase)       — derives key, verifies passphrase
//   vault.lock()                          — clear in-memory key
//   await vault.set(name, value)         — encrypted write
//   await vault.get<T>(name)             — decrypted read (or null)
//   await vault.delete(name)
//   await vault.listNames()              — names of encrypted entries
//   await vault.changePassphrase(old, n) — re-encrypt under new key
//   await vault.destroy()                — wipe ALL vault data
//
// The vault module owns nothing tenant-specific. It does not know what
// a "Plaid item" or a "SimpleFIN org" is. Tenants pick their own
// namespaced names (e.g. "plaid:credentials", "plaid:item:<item_id>",
// "simplefin:access-url") and the vault stores them under pf:vault:.

import { get, set, del, keys } from "idb-keyval";

// ── reserved metadata keys ─────────────────────────────────────────
const META_SALT = "pf:vault-meta:salt";
const META_CHECK = "pf:vault-meta:check";
const META_ITER = "pf:vault-meta:iter";

// Prefix under which encrypted tenant entries live. Anything matching
// this prefix is enumerable via listNames() and wiped on destroy().
const TENANT_PREFIX = "pf:vault:";

// Known plaintext used to verify the passphrase on unlock. If we can
// decrypt this to its known value, the derived key is right. Versioned
// so future format changes can co-exist.
const CHECK_PLAINTEXT = "pf-vault-check-v1";

// PBKDF2 iteration count for new vaults. Stored per-vault so it can be
// upgraded without breaking already-encrypted data.
export const DEFAULT_PBKDF2_ITERATIONS = 600_000;

// ── in-memory state (this whole module's lifetime) ─────────────────
let activeKey: CryptoKey | null = null;

// ── helpers ────────────────────────────────────────────────────────

interface EncryptedEnvelope {
  iv: string; // base64
  ct: string; // base64
}

function bytesToB64(bytes: Uint8Array): string {
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
}

function b64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function getSubtle(): SubtleCrypto {
  // Web Crypto is built into browsers and Node ≥19. We require Node 20
  // per package.json engines, so this is always defined. Defensive
  // throw helps tests that might run in a stripped env.
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (!c?.subtle) {
    throw new Error(
      "Web Crypto API is not available. The vault requires a modern browser or Node ≥20.",
    );
  }
  return c.subtle;
}

function randomBytes(len: number): Uint8Array {
  const out = new Uint8Array(len);
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (!c) throw new Error("crypto.getRandomValues is not available");
  c.getRandomValues(out);
  return out;
}

async function deriveKey(passphrase: string, salt: Uint8Array, iterations: number): Promise<CryptoKey> {
  const enc = new TextEncoder();
  const baseKey = await getSubtle().importKey(
    "raw",
    enc.encode(passphrase),
    { name: "PBKDF2" },
    false,
    ["deriveKey"],
  );
  return getSubtle().deriveKey(
    {
      name: "PBKDF2",
      salt: salt as BufferSource,
      iterations,
      hash: "SHA-256",
    },
    baseKey,
    { name: "AES-GCM", length: 256 },
    false, // not extractable — the key cannot leave this module
    ["encrypt", "decrypt"],
  );
}

async function encryptWithKey(key: CryptoKey, plaintext: string): Promise<EncryptedEnvelope> {
  const iv = randomBytes(12);
  const ct = await getSubtle().encrypt(
    { name: "AES-GCM", iv: iv as BufferSource },
    key,
    new TextEncoder().encode(plaintext),
  );
  return { iv: bytesToB64(iv), ct: bytesToB64(new Uint8Array(ct)) };
}

async function decryptWithKey(key: CryptoKey, env: EncryptedEnvelope): Promise<string> {
  const iv = b64ToBytes(env.iv);
  const ct = b64ToBytes(env.ct);
  const pt = await getSubtle().decrypt(
    { name: "AES-GCM", iv: iv as BufferSource },
    key,
    ct as BufferSource,
  );
  return new TextDecoder().decode(pt);
}

// ── public API ─────────────────────────────────────────────────────

/** True if a vault has been set up on this origin (salt + check exist). */
export async function isInitialized(): Promise<boolean> {
  const salt = await get<string | undefined>(META_SALT);
  const check = await get<EncryptedEnvelope | undefined>(META_CHECK);
  return Boolean(salt && check);
}

/** True if the vault is currently unlocked (a derived key is in memory). */
export function isUnlocked(): boolean {
  return activeKey !== null;
}

/** Set up a brand-new vault. Throws if one already exists; caller should
 *  check isInitialized() first. Leaves the vault unlocked on success. */
export async function initialize(passphrase: string): Promise<void> {
  if (!passphrase) throw new Error("Passphrase cannot be empty");
  if (await isInitialized()) {
    throw new Error("Vault is already initialized. Use unlock() or destroy() first.");
  }
  const salt = randomBytes(16);
  const key = await deriveKey(passphrase, salt, DEFAULT_PBKDF2_ITERATIONS);
  const check = await encryptWithKey(key, CHECK_PLAINTEXT);
  await Promise.all([
    set(META_SALT, bytesToB64(salt)),
    set(META_CHECK, check),
    set(META_ITER, DEFAULT_PBKDF2_ITERATIONS),
  ]);
  activeKey = key;
}

/** Derive the key from the passphrase and verify it against the stored
 *  check sentinel. On success, the vault is unlocked for subsequent
 *  get/set calls. Throws on wrong passphrase or missing vault. */
export async function unlock(passphrase: string): Promise<void> {
  if (!passphrase) throw new Error("Passphrase cannot be empty");
  const saltB64 = await get<string | undefined>(META_SALT);
  const check = await get<EncryptedEnvelope | undefined>(META_CHECK);
  const iter = (await get<number | undefined>(META_ITER)) ?? DEFAULT_PBKDF2_ITERATIONS;
  if (!saltB64 || !check) {
    throw new Error("Vault has not been initialized on this device.");
  }
  const key = await deriveKey(passphrase, b64ToBytes(saltB64), iter);
  // Decrypt the check sentinel — wrong key throws OperationError (GCM
  // tag failure). We translate to a clearer message.
  let plaintext: string;
  try {
    plaintext = await decryptWithKey(key, check);
  } catch {
    throw new Error("Incorrect passphrase.");
  }
  if (plaintext !== CHECK_PLAINTEXT) {
    // Should be impossible if GCM tag verified, but belt + suspenders.
    throw new Error("Vault check sentinel did not match. Storage may be corrupted.");
  }
  activeKey = key;
}

/** Clear the in-memory key. After this, get/set throw until unlock(). */
export function lock(): void {
  activeKey = null;
}

function requireUnlocked(): CryptoKey {
  if (!activeKey) {
    throw new Error("Vault is locked. Call unlock(passphrase) first.");
  }
  return activeKey;
}

/** Encrypt and store a JSON-serialisable value under `name`. The full
 *  IndexedDB key is `pf:vault:<name>`. Throws if locked. */
export async function setValue<T>(name: string, value: T): Promise<void> {
  const key = requireUnlocked();
  validateTenantName(name);
  const env = await encryptWithKey(key, JSON.stringify(value));
  await set(TENANT_PREFIX + name, env);
}

/** Read and decrypt the value previously stored under `name`. Returns
 *  null if absent. Throws if locked or if the ciphertext is corrupt. */
export async function getValue<T>(name: string): Promise<T | null> {
  const key = requireUnlocked();
  validateTenantName(name);
  const env = await get<EncryptedEnvelope | undefined>(TENANT_PREFIX + name);
  if (!env) return null;
  const plaintext = await decryptWithKey(key, env);
  return JSON.parse(plaintext) as T;
}

/** Delete an entry. No-op if absent. Does not require the vault to be
 *  unlocked — deletion is safe regardless. */
export async function deleteValue(name: string): Promise<void> {
  validateTenantName(name);
  await del(TENANT_PREFIX + name);
}

/** Enumerate the names of all currently-encrypted entries. Useful for
 *  changePassphrase (which has to re-encrypt every one) and for a
 *  hypothetical "what is the vault holding" debug view. */
export async function listNames(): Promise<string[]> {
  const all = (await keys()) as IDBValidKey[];
  const out: string[] = [];
  for (const k of all) {
    if (typeof k === "string" && k.startsWith(TENANT_PREFIX)) {
      out.push(k.slice(TENANT_PREFIX.length));
    }
  }
  return out.sort();
}

/** Verify the OLD passphrase, then re-encrypt every entry under a new
 *  derived key, then swap metadata atomically-ish. The vault is left
 *  unlocked under the new key on success. */
export async function changePassphrase(oldPassphrase: string, newPassphrase: string): Promise<void> {
  if (!newPassphrase) throw new Error("New passphrase cannot be empty");
  // Force a fresh unlock with the old passphrase so we don't trust an
  // already-unlocked state passed in by the caller.
  await unlock(oldPassphrase);
  const oldKey = requireUnlocked();

  const newSalt = randomBytes(16);
  const newKey = await deriveKey(newPassphrase, newSalt, DEFAULT_PBKDF2_ITERATIONS);
  const newCheck = await encryptWithKey(newKey, CHECK_PLAINTEXT);

  // Decrypt every entry under oldKey, re-encrypt under newKey, write back.
  // No transaction guarantee in IndexedDB across keys with idb-keyval, so
  // we write the new metadata last. If we crash mid-flight, the worst
  // case is half the entries are unreadable — the user re-links banks.
  const names = await listNames();
  for (const name of names) {
    const env = await get<EncryptedEnvelope | undefined>(TENANT_PREFIX + name);
    if (!env) continue;
    const plaintext = await decryptWithKey(oldKey, env);
    const reEnc = await encryptWithKey(newKey, plaintext);
    await set(TENANT_PREFIX + name, reEnc);
  }
  await Promise.all([
    set(META_SALT, bytesToB64(newSalt)),
    set(META_CHECK, newCheck),
    set(META_ITER, DEFAULT_PBKDF2_ITERATIONS),
  ]);
  activeKey = newKey;
}

/** Wipe every vault-owned IndexedDB key — metadata + tenant entries.
 *  Used by the "forgot passphrase, relink everything" recovery path.
 *  Does NOT require the vault to be unlocked. */
export async function destroy(): Promise<void> {
  const names = await listNames();
  await Promise.all(names.map((n) => del(TENANT_PREFIX + n)));
  await Promise.all([del(META_SALT), del(META_CHECK), del(META_ITER)]);
  activeKey = null;
}

// Tenant names live inside our `pf:vault:` namespace. Disallow names
// that would collide with the reserved metadata keys (`-meta` prefix
// isn't reachable through this validator but we forbid `:` at the
// start to keep room for future namespacing). Empty names are rejected.
function validateTenantName(name: string): void {
  if (!name) throw new Error("Vault entry name cannot be empty");
  if (name.startsWith(":") || name.startsWith("_")) {
    throw new Error(`Reserved vault entry name: ${name}`);
  }
}

// Testing seam — vitest needs to reset module state between tests, but
// the tenant data lives in idb-keyval (test-mocked). Resetting the
// in-memory key is enough.
export const _testing = {
  resetMemory(): void {
    activeKey = null;
  },
};
